import { useEffect, useRef } from "react";
import { withoutEventInvites, withoutOccurrenceInvites, withoutSeriesInvites } from "./eventInvites";

// App-wide "an Event changed" signal. EventFocusModal can be opened from
// Calendar, Home, the invite inbox or a notification, and ModalManager props
// are a snapshot — so instead of threading refresh callbacks through every
// opener, the modal announces each successful mutation here and the pages
// that show events (Calendar, Home) re-read from the backend. Nothing is
// reconstructed locally: the only local patches are confirmed occurrence
// results (below), applied so a failed re-read can't leave stale state.
//
// detail: { eventId, kind, seriesRsvp? } — kind is one of
//   "updated"  the Event or one occurrence changed (edit, cancel, RSVP);
//              `seriesRsvp` is the confirmed series RSVP ({ status,
//              reminder }) after the viewer answered the series
//   "deleted"  the Event is gone (or no longer visible to this user)
//   "trimmed"  delete-future: the series ended, future occurrences removed
// and, for the viewer's participation in one occurrence (occurrenceActions),
// detail: { eventId, occurrenceId, occurrence?, kind } with kind
//   "occurrenceUpdated"     their participation changed; `occurrence` is the
//                           confirmed result, applied at once, then re-read
//   "occurrenceAccessLost"  they no longer see this occurrence (an
//                           occurrence-only guest removed it, or it's gone):
//                           dropped at once, whatever a later re-read says
// Only the occurrence named is affected — never its series or other dates.
const EVENT_NAME = "blorp:events-changed";

export function announceEventChange(detail) {
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));
}

// Calls `handler(detail)` on every announced change while mounted.
export function useEventChanges(handler) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const listener = (e) => handlerRef.current?.(e.detail || {});
    window.addEventListener(EVENT_NAME, listener);
    return () => window.removeEventListener(EVENT_NAME, listener);
  }, []);
}

// ---------------------------------------------------------------------------
// Applying an announced change to a page's own copies, before its re-read.
// ---------------------------------------------------------------------------

// Calendar's occurrences (the visible range): a lost occurrence is dropped, a
// confirmed occurrence result replaces its copy. Everything else is left to
// the re-read.
export function applyChangeToOccurrences(occurrences, change) {
  const { kind, occurrenceId, occurrence } = change || {};
  if (kind === "occurrenceAccessLost") return occurrences.filter((o) => o.id !== occurrenceId);
  if (kind === "occurrenceUpdated" && occurrence) {
    return occurrences.map((o) => (o.id === occurrence.id ? { ...o, ...occurrence } : o));
  }
  return occurrences;
}

// The invite inbox: a deleted Event takes its invitations of both kinds with
// it; an occurrence the viewer answered (or can no longer see) resolves its
// own invitation only, and a series answer its series invitation only —
// never one for the other.
export function applyChangeToInvites(invites, change) {
  const { kind, eventId, occurrenceId, occurrence, seriesRsvp } = change || {};
  switch (kind) {
    case "deleted":
      return withoutEventInvites(invites, eventId);
    case "updated":
      return seriesRsvp?.status && seriesRsvp.status !== "invited" ? withoutSeriesInvites(invites, eventId) : invites;
    case "occurrenceAccessLost":
      return withoutOccurrenceInvites(invites, occurrenceId);
    case "occurrenceUpdated":
      return occurrence && occurrence.my_rsvp_status !== "invited"
        ? withoutOccurrenceInvites(invites, occurrence.id)
        : invites;
    default:
      return invites;
  }
}
